/**
 * "Credit position": where a payer's credit account stands against its limit
 * (docs/features/v1-completion/L_credit_control_spec.md §4.6, mock-up
 * L_credit_control.html states 7 and 8). Four figures, VAT included: what it owes on
 * CIS invoices ("Invoiced, unpaid"), what it has ordered and not been invoiced for
 * ("Ordered, not invoiced yet"), then, on Settings, what is used, and on the credit
 * ticket's decision panel, the limit; last, what is left ("Left to order"), in red
 * when it is negative, with the sentence that says what over limit means.
 *
 * Every caption and the over-limit sentence arrive with the exposure (credit
 * control's words); this lays them out. When the read failed the response says so
 * instead of a strip, and the page still works.
 */
import { Link } from "react-router";
import type { AccountExposure } from "@shared-core/data/credit-control-types";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { cn } from "../../primitives/utils";

export type ExposureStripVariant = "settings" | "ticket";

function Tile({
  caption,
  value,
  isOver = false,
}: {
  caption: string;
  value: string;
  isOver?: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
      <dt className="text-xs text-muted-foreground">{caption}</dt>
      <dd className={cn("text-lg tabular-nums", isOver && "text-destructive")}>
        {value}
      </dd>
    </div>
  );
}

export function ExposureStrip({
  exposure,
  unavailableNote,
  variant,
  agedDebtSearch,
  agedDebtNote = null,
}: {
  exposure: AccountExposure | null;
  /** Credit control's sentence for "could not be read just now"; shown in the strip's place. */
  unavailableNote: string | null;
  /** Settings shows "Used"; the credit ticket shows the "Limit" beside what is left. */
  variant: ExposureStripVariant;
  /** What the "See the aged debt" link searches for: the payer's billing account ref. */
  agedDebtSearch?: string;
  /** The credit ticket's sentence on how much of what the payer owes is old and disputed (views builds it). */
  agedDebtNote?: string | null;
}) {
  if (!exposure) {
    return unavailableNote ? (
      <p role="status" className="text-sm text-muted-foreground">
        {unavailableNote}
      </p>
    ) : null;
  }
  const third =
    variant === "settings"
      ? { caption: "Used", pence: exposure.usedPence }
      : { caption: "Limit", pence: exposure.limitPence };
  return (
    <section aria-labelledby="exposure-strip-heading" className="space-y-2">
      <h3 id="exposure-strip-heading" className="font-medium">
        Credit position
      </h3>
      <dl className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        <Tile
          caption={exposure.receivablesLabel}
          value={formatQuotedPrice(exposure.receivablesPence)}
        />
        <Tile
          caption={exposure.committedLabel}
          value={formatQuotedPrice(exposure.committedPence)}
        />
        <Tile
          caption={third.caption}
          value={third.pence === null ? "–" : formatQuotedPrice(third.pence)}
        />
        <Tile
          caption={exposure.headroomLabel}
          value={
            exposure.headroomPence === null
              ? "–"
              : formatQuotedPrice(exposure.headroomPence)
          }
          isOver={exposure.isOverLimit}
        />
      </dl>
      {exposure.overLimitNote && (
        <p role="status" className="text-sm text-destructive">
          {exposure.overLimitNote}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {agedDebtNote ?? (
          <>
            VAT included. Only invoices and orders in One are counted, not Sage
            balances.
          </>
        )}{" "}
        <Link
          to={paths.financeAgedDebt(
            agedDebtSearch ? { q: agedDebtSearch } : undefined,
          )}
          className="text-primary hover:underline"
        >
          See the aged debt
        </Link>
        .
      </p>
    </section>
  );
}
